import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { theme } from "@polifonia/theme";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InfoBox } from "@/components/InfoBox";
import type { ModalProps } from "@/components/Modal";
import { Modal } from "@/components/Modal";
import { Select } from "@/components/Select";
import { Typography } from "@/components/Typography";
import { useEnableCustomFieldSingleSelect } from "@/features/customField/useEnableCustomFieldSingleSelect";
import { handleNonGlobalApiError } from "@/features/error/handleError";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { ModalFooter } from "@/pages/applications/pages/customFields/components/ModalFooter";
import { sharedStyles } from "@/pages/applications/pages/customFields/components/sharedStyles";
import { SingleSelectOptionEditor } from "@/pages/applications/pages/customFields/components/SingleSelectOptionEditor";
import {
  CUSTOM_FIELD_DESCRIPTION_MAX_LENGTH,
  CUSTOM_FIELD_KEY_PREFIX,
  CUSTOM_FIELD_NAME_MAX_LENGTH,
  CUSTOM_FIELD_USER_KEY_MAX_LENGTH,
} from "@/pages/applications/pages/customFields/constants";
import {
  transformNameToKey,
  useAutoGenerateKey,
} from "@/pages/applications/pages/customFields/hooks/useAutoGenerateKey";
import type { SingleSelectOptionsDraft } from "@/pages/applications/pages/customFields/utils/singleSelectOptions";
import {
  createOptionDraft,
  isOptionsDraftValid,
} from "@/pages/applications/pages/customFields/utils/singleSelectOptions";

// Valid key regex: only letters, numbers, and underscores
const KEY_REGEX = /^[A-Za-z0-9_]*$/;

type CustomFieldType = InterludeTypes["CustomFieldType"];

interface FormValues {
  name: string;
  key: string;
  type: CustomFieldType;
  description: string;
  options: SingleSelectOptionsDraft;
}

/**
 * A single select field must arrive with at least one option, because its type
 * cannot be changed afterwards -- there is no "create it now, add options
 * later". So the editor opens on one empty row rather than an empty list.
 */
function initialOptionsDraft(): SingleSelectOptionsDraft {
  return { active: [createOptionDraft()], archived: [] };
}

interface CreateFieldModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateFieldModal: React.FC<CreateFieldModalProps> =
  function CreateFieldModal({ open, onClose, onSuccess }) {
    const { t } = useTranslation();
    const [apiError, setApiError] = useState<string | null>(null);

    const {
      control,
      handleSubmit,
      reset,
      watch,
      setValue,
      formState: { isValid },
    } = useForm<FormValues>({
      mode: "onChange",
      defaultValues: {
        name: "",
        key: "",
        type: "text",
        description: "",
        options: initialOptionsDraft(),
      },
    });

    const autoGenerateKey = useAutoGenerateKey();

    const watchedName = watch("name");
    const watchedKey = watch("key");
    const watchedDescription = watch("description");
    const watchedType = watch("type");
    const watchedOptions = watch("options");

    const enableSingleSelect = useEnableCustomFieldSingleSelect();
    const isSingleSelect = watchedType === "single_select";

    // Auto-generate key when name changes
    useEffect(() => {
      if (!autoGenerateKey.isAutoGenerateEnabled) {
        return;
      }
      const generatedKey = transformNameToKey(
        watchedName,
        CUSTOM_FIELD_USER_KEY_MAX_LENGTH,
      );
      setValue("key", generatedKey, { shouldValidate: true });
    }, [watchedName, autoGenerateKey.isAutoGenerateEnabled, setValue]);

    const typeOptions = useMemo(
      () => [
        {
          value: "text" as const,
          label: t("customField.fieldType.text"),
          description: t("customField.fieldType.text.example"),
        },
        {
          value: "float" as const,
          label: t("customField.fieldType.number"),
          description: t("customField.fieldType.number.example"),
        },
        {
          value: "date" as const,
          label: t("customField.fieldType.date"),
          description: t("customField.fieldType.date.example"),
        },
        {
          value: "timestamp" as const,
          label: t("customField.fieldType.dateTime"),
          description: t("customField.fieldType.dateTime.example"),
        },
        // Creation is the only thing the gate blocks. An existing single
        // select field still shows and still edits with the gate off.
        ...(enableSingleSelect
          ? [
              {
                value: "single_select" as const,
                label: t("customField.fieldType.singleSelect"),
                description: t("customField.fieldType.singleSelect.example"),
              },
            ]
          : []),
      ],
      [t, enableSingleSelect],
    );

    const createMutation = interlude.customField.useCreateCustomField(
      undefined,
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
        onError: async (error) => {
          await handleNonGlobalApiError(error, {
            CUSTOM_FIELD_KEY_DUPLICATE() {
              setApiError(t("customField.error.keyDuplicate"));
            },
            CUSTOM_FIELD_NAME_DUPLICATE() {
              setApiError(t("customField.error.nameDuplicate"));
            },
            CUSTOM_FIELD_ACTIVE_LIMIT_EXCEEDED() {
              setApiError(t("customField.page.limitReached"));
            },
            CUSTOM_FIELD_SLOT_LIMIT_EXCEEDED() {
              setApiError(t("customField.error.slotLimitExceeded"));
            },
            /**
             * The editor already rejects a duplicate across both sections
             * before submit, so this is only reachable when the server knows
             * about an option the client does not. It still needs a handler:
             * the code is not mapped globally any more, so without one the
             * save would fail with nothing on screen.
             */
            CUSTOM_FIELD_SINGLE_SELECT_OPTIONS_DUPLICATE() {
              setApiError(t("customField.error.optionsDuplicate"));
            },
          });
        },
      },
    );

    const handleClose = useHandler(() => {
      // `reset()` with no argument restores the mounted defaults, which would
      // hand the next open the same row objects. A fresh draft gives it new
      // `rowId`s instead.
      reset({
        name: "",
        key: "",
        type: "text",
        description: "",
        options: initialOptionsDraft(),
      });
      setApiError(null);
      autoGenerateKey.resetAutoGenerate();
      onClose();
    });

    const handleModalClose = useHandler<ModalProps["handleClose"]>(
      (_event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        handleClose();
      },
    );

    const onSubmit = useHandler((values: FormValues) => {
      setApiError(null);
      createMutation.mutate({
        name: values.name.trim(),
        key: `${CUSTOM_FIELD_KEY_PREFIX}${values.key.trim()}`,
        type: values.type,
        description: values.description.trim(),
        // Only single select carries options, and creation accepts only
        // `name` and `description`: the server assigns the key and makes
        // every new option active.
        //
        // So only the active section is sent. The editor lets a row be
        // archived here too, and there is no way to express that on a field
        // that does not exist yet -- sending such a row would create it
        // active, which is the opposite of what the user asked for. Leaving
        // it out is the closest honest reading.
        ...(values.type === "single_select"
          ? {
              options: values.options.active.map((option) => ({
                name: option.name.trim(),
                description: option.description.trim(),
              })),
            }
          : {}),
      });
    });

    const footer: React.ReactNode = useMemo(
      () => (
        <ModalFooter
          onCancel={handleClose}
          onSubmit={handleSubmit(onSubmit)}
          cancelKey="customField.createModal.cancel"
          submitKey="customField.createModal.submit"
          isValid={
            isValid &&
            (!isSingleSelect ||
              // Weigh exactly what gets sent. Creation posts the active
              // section only, so validating the whole draft blocks submits it
              // has no business blocking: a name shared with an archived row
              // (only one of the two is sent), an archived row left unnamed,
              // or a combined count over the limit while the active section
              // is under it. Passing an empty `archived` also makes
              // `isOptionsDraftValid`'s own `total > 0` guard cover the
              // "nothing active to send" case.
              isOptionsDraftValid({
                active: watchedOptions.active,
                archived: [],
              }))
          }
          isLoading={createMutation.isPending}
        />
      ),
      [
        handleClose,
        handleSubmit,
        onSubmit,
        isValid,
        isSingleSelect,
        watchedOptions,
        createMutation.isPending,
      ],
    );

    return (
      <Modal
        open={open}
        handleClose={handleModalClose}
        title={t("customField.createModal.title")}
        width={520}
        footer={footer}
      >
        <Stack gap="16px" paddingBottom="8px">
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t("customField.createModal.description")}
          </Typography>

          {apiError ? (
            <InfoBox
              variant="error"
              title={t("customField.error.configurationFailed")}
              description={apiError}
            />
          ) : null}

          {/* Name Field */}
          <Box sx={sharedStyles.formSection}>
            <Box sx={sharedStyles.label}>
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgBody}
              >
                {t("customField.createModal.name.label")}
              </Typography>
              <Box component="span" sx={sharedStyles.required}>
                *
              </Box>
            </Box>
            <Controller
              name="name"
              control={control}
              rules={{
                required: t("customField.error.nameRequired"),
                maxLength: CUSTOM_FIELD_NAME_MAX_LENGTH,
                validate: (value) =>
                  value.trim().length > 0 ||
                  t("customField.error.nameRequired"),
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  onChange={(e) => {
                    // Auto-trim leading whitespace while preserving middle spaces
                    const value = e.target.value;
                    const trimmedValue = value.trimStart();
                    field.onChange({
                      ...e,
                      target: { ...e.target, value: trimmedValue },
                    });
                  }}
                  onBlur={(e) => {
                    // Trim trailing whitespace on blur
                    const trimmedValue = e.target.value.trimEnd();
                    if (trimmedValue !== e.target.value) {
                      field.onChange({
                        ...e,
                        target: { ...e.target, value: trimmedValue },
                      });
                    }
                    field.onBlur();
                  }}
                  fullWidth
                  placeholder={t("customField.createModal.name.placeholder")}
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{
                    htmlInput: {
                      maxLength: CUSTOM_FIELD_NAME_MAX_LENGTH,
                    },
                    input: {
                      endAdornment: (
                        <Box component="span" sx={sharedStyles.charCount}>
                          <Typography variant="note" color="inherit">
                            {watchedName.length}/{CUSTOM_FIELD_NAME_MAX_LENGTH}
                          </Typography>
                        </Box>
                      ),
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* Key Field */}
          <Box sx={sharedStyles.formSection}>
            <Box sx={sharedStyles.label}>
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgBody}
              >
                {t("customField.createModal.key.label")}
              </Typography>
              <Box component="span" sx={sharedStyles.required}>
                *
              </Box>
            </Box>
            <Controller
              name="key"
              control={control}
              rules={{
                required: t("customField.error.keyRequired"),
                maxLength: CUSTOM_FIELD_USER_KEY_MAX_LENGTH,
                validate: (value) => {
                  if (value.trim().length === 0) {
                    return t("customField.error.keyRequired");
                  }
                  if (!KEY_REGEX.test(value)) {
                    return t("customField.error.invalidKeyCharacters");
                  }
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Box sx={sharedStyles.keyInputWrapper}>
                    <Box component="span" sx={sharedStyles.keyPrefix}>
                      {CUSTOM_FIELD_KEY_PREFIX}
                    </Box>
                    <TextField
                      {...field}
                      onChange={(e) => {
                        // Disable auto-generation when user manually edits
                        autoGenerateKey.disableAutoGenerate();
                        field.onChange(e);
                      }}
                      sx={sharedStyles.keyInput}
                      fullWidth
                      placeholder={t("customField.createModal.key.placeholder")}
                      error={!!error}
                      slotProps={{
                        htmlInput: {
                          maxLength: CUSTOM_FIELD_USER_KEY_MAX_LENGTH,
                        },
                        input: {
                          endAdornment: (
                            <Box component="span" sx={sharedStyles.charCount}>
                              <Typography variant="note" color="inherit">
                                {watchedKey.length}/
                                {CUSTOM_FIELD_USER_KEY_MAX_LENGTH}
                              </Typography>
                            </Box>
                          ),
                        },
                      }}
                    />
                  </Box>
                  {error ? (
                    <FormHelperText error>{error.message}</FormHelperText>
                  ) : (
                    <FormHelperText>
                      {t("customField.createModal.key.hint")}
                    </FormHelperText>
                  )}
                </>
              )}
            />
          </Box>

          {/* Type Field */}
          <Box sx={sharedStyles.formSection}>
            <Box sx={sharedStyles.label}>
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgBody}
              >
                {t("customField.createModal.type.label")}
              </Typography>
              <Box component="span" sx={sharedStyles.required}>
                *
              </Box>
            </Box>
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  options={typeOptions}
                  value={field.value}
                  onChange={field.onChange}
                  disableClearable
                  helperText={t("customField.createModal.type.hint")}
                />
              )}
            />
          </Box>

          {/* Option list, single select only. The editor renders its own
              "Options" label and the n/200 counter, so there is no label
              wrapper here -- adding one showed the heading twice. */}
          {isSingleSelect ? (
            <Controller
              name="options"
              control={control}
              render={({ field }) => (
                <SingleSelectOptionEditor
                  value={field.value}
                  onChange={field.onChange}
                  disabled={createMutation.isPending}
                />
              )}
            />
          ) : null}

          {/* Description Field */}
          <Box sx={sharedStyles.formSection}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgBody}
            >
              {t("customField.createModal.description.label")}
            </Typography>
            <Controller
              name="description"
              control={control}
              rules={{
                maxLength: CUSTOM_FIELD_DESCRIPTION_MAX_LENGTH,
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  sx={sharedStyles.descriptionInput}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={t(
                    "customField.createModal.description.placeholder",
                  )}
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{
                    htmlInput: {
                      maxLength: CUSTOM_FIELD_DESCRIPTION_MAX_LENGTH,
                    },
                    input: {
                      endAdornment: (
                        <Box
                          component="span"
                          sx={sharedStyles.charCountWithPadding}
                        >
                          <Typography variant="note" color="inherit">
                            {watchedDescription.length}/
                            {CUSTOM_FIELD_DESCRIPTION_MAX_LENGTH}
                          </Typography>
                        </Box>
                      ),
                    },
                  }}
                />
              )}
            />
          </Box>
        </Stack>
      </Modal>
    );
  };
